import type { UsuarioSessao } from '../session.js';
import type { Notificacao } from '@pgcustom/shared-types';

/**
 * `<erp-header>` — cabeçalho da área logada: título/contexto da página
 * atual, busca, notificações e usuário autenticado.
 *
 * "Busca" é um atalho visual para o Command Palette real (Cmd/Ctrl+K,
 * `main.ts`) — nenhum endpoint de busca global existe no backend, então
 * este componente não busca nada sozinho: só emite `erp-abrir-busca` ao
 * ser clicado, e quem decide o que fazer é quem monta o shell (mesmo
 * padrão de `erp-sair`/`erp-navegar`). Notificações passaram a ser reais na Sprint 13
 * (`/api/v1/notificacoes`, `DECISIONS.md` DEC-032) — o sino mostra a
 * contagem real de não lidas e abre um popover com a lista real; quem
 * busca/atualiza a lista e chama `POST .../lida` é quem monta o shell
 * (`main.ts`, mesmo padrão de `erp-sair`), este componente só emite
 * `erp-notificacao-lida` com o id clicado.
 *
 * Emite `erp-sair` ao clicar em "Sair" — quem decide o que fazer
 * (chamar `POST /auth/logout`, limpar sessão, navegar para o login) é
 * quem monta o shell, nunca este componente.
 *
 * O ícone de engrenagem emite `erp-navegar` com `detail: { caminho:
 * '/admin/configuracoes' }` — mesmo evento/formato de `erp-sidebar`,
 * para a rota real já existente no menu (RBAC/tela própria decidem o
 * resto). Nenhum ícone de "mensagens" foi adicionado: não existe módulo
 * de mensageria no ERP — um ícone assim seria uma funcionalidade
 * fabricada, sem endpoint por trás.
 */
export class ErpHeader extends HTMLElement {
  private readonly root: ShadowRoot;
  private _titulo = '';
  private _contexto = '';
  private _usuario: UsuarioSessao | null = null;
  private _notificacoes: Notificacao[] = [];
  private popoverAberto = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set titulo(valor: string) {
    this._titulo = valor;
    this.render();
  }

  set contexto(valor: string) {
    this._contexto = valor;
    this.render();
  }

  set usuario(valor: UsuarioSessao | null) {
    this._usuario = valor;
    this.render();
  }

  set notificacoes(valor: Notificacao[]) {
    this._notificacoes = valor;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    const usuario = this._usuario;
    const naoLidas = this._notificacoes.filter((n) => !n.lida).length;

    this.root.innerHTML = `
      <style>
        :host {
          display: block;
          background: var(--pg-color-bg-base, #121417);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .barra {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--pg-space-5, 24px);
          padding: var(--pg-space-4, 16px) var(--pg-space-6, 32px);
        }
        .contexto { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .titulo {
          font-size: var(--pg-font-size-lg, 1.125rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .subtitulo {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .subtitulo:empty { display: none; }
        .acoes { display: flex; align-items: center; gap: var(--pg-space-4, 16px); flex-shrink: 0; }
        .busca {
          display: flex;
          align-items: center;
          gap: var(--pg-space-2, 8px);
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-desabilitado, #5c626b);
          font-family: inherit;
          font-size: var(--pg-font-size-sm, 0.875rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          width: 240px;
          cursor: pointer;
          transition: border-color 0.15s ease, color 0.15s ease;
        }
        .busca:hover, .busca:focus-visible { color: var(--pg-color-texto-secundario, #9aa1ab); border-color: var(--pg-color-texto-desabilitado, #5c626b); outline: none; }
        .busca:focus-visible { box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35)); }
        .busca-icone { flex-shrink: 0; display: flex; width: 14px; height: 14px; }
        .busca-icone svg { width: 100%; height: 100%; }
        .busca-rotulo { flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .busca-atalho {
          flex-shrink: 0;
          font-size: 10px;
          font-family: var(--pg-font-familia-mono, ui-monospace);
          color: var(--pg-color-texto-desabilitado, #5c626b);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-sm, 6px);
          padding: 1px 5px;
        }
        .notificacoes { position: relative; }
        .icone-notificacao {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          width: 36px;
          height: 36px;
          cursor: pointer;
        }
        .icone-notificacao:hover { color: var(--pg-color-texto-primario, #f2f3f5); border-color: var(--pg-color-texto-secundario, #9aa1ab); }
        .icone-notificacao:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .contador {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 16px;
          height: 16px;
          padding: 0 3px;
          border-radius: var(--pg-radius-full, 9999px);
          background: var(--pg-color-erro, #e5484d);
          color: #ffffff;
          font-size: 10px;
          font-weight: var(--pg-font-weight-semibold, 600);
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }
        .popover {
          position: absolute;
          top: calc(100% + var(--pg-space-2, 8px));
          right: 0;
          width: 320px;
          max-height: 360px;
          overflow-y: auto;
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
          z-index: 10;
        }
        .popover-titulo {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          padding: var(--pg-space-3, 12px) var(--pg-space-3, 12px) 0;
        }
        .popover-vazio {
          padding: var(--pg-space-4, 16px) var(--pg-space-3, 12px);
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-desabilitado, #5c626b);
        }
        .item-notificacao {
          display: block;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          padding: var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .item-notificacao:last-child { border-bottom: none; }
        .item-notificacao:hover { background: var(--pg-color-bg-base, #121417); }
        .item-notificacao:focus-visible {
          outline: none;
          background: var(--pg-color-bg-base, #121417);
          box-shadow: inset var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .item-notificacao.lida { opacity: 0.55; cursor: default; }
        .item-titulo { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-primario, #f2f3f5); }
        .item-mensagem { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .item-data { font-size: 10px; color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: 4px; }
        .usuario { display: flex; align-items: center; gap: var(--pg-space-3, 12px); }
        .usuario-info { display: flex; flex-direction: column; align-items: flex-end; line-height: var(--pg-line-height-apertado, 1.2); }
        .usuario-nome { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-primario, #f2f3f5); }
        .usuario-papel { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); text-transform: capitalize; }
        .avatar {
          width: 32px;
          height: 32px;
          border-radius: var(--pg-radius-full, 9999px);
          background: var(--pg-color-destaque, #2f8cff);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--pg-font-size-sm, 0.875rem);
          font-weight: var(--pg-font-weight-semibold, 600);
        }
        .sair {
          background: none;
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          font-size: var(--pg-font-size-xs, 0.75rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .sair:hover { color: var(--pg-color-texto-primario, #f2f3f5); border-color: var(--pg-color-texto-secundario, #9aa1ab); }
        .sair:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        /* Mesmo breakpoint de erp-shell.ts/erp-sidebar.ts — esconde só o
           texto auxiliar (rótulo/atalho de busca, nome/papel do usuário),
           mantém ícones/avatar/ação sempre visíveis e funcionais. */
        @media (max-width: 900px) {
          .barra { padding: var(--pg-space-3, 12px) var(--pg-space-4, 16px); gap: var(--pg-space-3, 12px); }
          .busca-rotulo, .busca-atalho, .usuario-info { display: none; }
          .busca { padding: var(--pg-space-2, 8px); }
        }
      </style>
      <div class="barra" part="barra">
        <div class="contexto">
          <span class="titulo">${escaparHtml(this._titulo)}</span>
          <span class="subtitulo">${escaparHtml(this._contexto)}</span>
        </div>
        <div class="acoes">
          <button class="busca" type="button" id="botao-busca" aria-label="Abrir busca (Ctrl+K)">
            <span class="busca-icone">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <span class="busca-rotulo">Buscar ou navegar…</span>
            <span class="busca-atalho">Ctrl K</span>
          </button>
          <div class="notificacoes">
            <button class="icone-notificacao" type="button" title="Notificações" aria-label="Notificações">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              ${naoLidas > 0 ? `<span class="contador">${naoLidas > 9 ? '9+' : naoLidas}</span>` : ''}
            </button>
            ${this.popoverAberto ? this.renderPopover() : ''}
          </div>
          <button class="icone-notificacao" type="button" id="botao-configuracoes" title="Configurações" aria-label="Configurações">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          ${
            usuario
              ? `
            <div class="usuario">
              <div class="usuario-info">
                <span class="usuario-nome">${escaparHtml(usuario.nome)}</span>
                <span class="usuario-papel">${escaparHtml(usuario.papel)}</span>
              </div>
              <div class="avatar">${escaparHtml(iniciais(usuario.nome))}</div>
              <button class="sair" type="button" id="botao-sair">Sair</button>
            </div>`
              : ''
          }
        </div>
      </div>
    `;

    this.root.querySelector('#botao-busca')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('erp-abrir-busca', { bubbles: true, composed: true }));
    });

    this.root.querySelector('#botao-sair')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('erp-sair', { bubbles: true, composed: true }));
    });

    this.root.querySelector('#botao-configuracoes')?.addEventListener('click', () => {
      this.dispatchEvent(
        new CustomEvent('erp-navegar', {
          bubbles: true,
          composed: true,
          detail: { caminho: '/admin/configuracoes' },
        })
      );
    });

    this.root.querySelector('.icone-notificacao')?.addEventListener('click', (evento) => {
      evento.stopPropagation();
      this.popoverAberto = !this.popoverAberto;
      this.render();
    });

    this.root.querySelectorAll<HTMLButtonElement>('.item-notificacao[data-id]').forEach((item) => {
      item.addEventListener('click', () => {
        const id = item.dataset['id']!;
        const lida = item.dataset['lida'] === '1';
        if (lida) return;
        this.dispatchEvent(new CustomEvent('erp-notificacao-lida', { detail: { id }, bubbles: true, composed: true }));
      });
    });

    if (this.popoverAberto) {
      // Fecha o popover ao clicar fora — listener único no `document`, removido quando o popover fecha (evita acumular listeners a cada render).
      const fechar = (evento: MouseEvent): void => {
        if (evento.composedPath().includes(this)) return;
        this.popoverAberto = false;
        this.render();
        document.removeEventListener('click', fechar);
      };
      document.addEventListener('click', fechar);
    }
  }

  private renderPopover(): string {
    if (this._notificacoes.length === 0) {
      return `<div class="popover"><p class="popover-vazio">Nenhuma notificação ainda.</p></div>`;
    }

    const itens = this._notificacoes
      .slice(0, 20)
      .map(
        (n) => `
        <button class="item-notificacao ${n.lida ? 'lida' : ''}" type="button" data-id="${n.id}" data-lida="${n.lida ? '1' : '0'}">
          <div class="item-titulo">${escaparHtml(n.titulo)}</div>
          <div class="item-mensagem">${escaparHtml(n.mensagem)}</div>
          <div class="item-data">${new Date(n.criadoEm).toLocaleString('pt-BR')}</div>
        </button>`
      )
      .join('');

    return `<div class="popover"><p class="popover-titulo">Notificações</p>${itens}</div>`;
  }
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-header', ErpHeader);

declare global {
  interface HTMLElementTagNameMap {
    'erp-header': ErpHeader;
  }
}
